import { Outlet, useParams, useLocation } from 'react-router-dom'
import { useProject } from '@/hooks/api/use-projects'
import { Breadcrumbs } from '@/components/layout/breadcrumbs'
import { ProjectTabs } from '@/components/layout/project-tabs'
import { ProjectContext } from '@/hooks/use-project-context'
import { ErrorBoundaryClass } from '@/components/ErrorBoundary'
import { isFeatureAvailable, getUnlockPhase } from '@/lib/phase-features'
import { PHASE_LABELS } from '@/lib/constants'
import type { Phase } from '@/lib/constants'
import { Lock } from 'lucide-react'

/** Extract the feature key from the current pathname (first segment after /hub/projects/:id/) */
function getActiveFeatureKey(pathname: string, projectId: string): string | null {
  const prefix = `/hub/projects/${projectId}/`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length)
  const segment = rest.split('/')[0]
  return segment || null
}

export function ProjectLayout() {
  const { id: projectId } = useParams<{ id: string }>()
  const { pathname } = useLocation()
  const { data, isLoading } = useProject(projectId!)

  const project = data?.project ?? null
  const userRole = project?.user_role ?? 'worker'
  const phase = (project?.phase as Phase) ?? undefined

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-5 w-48 animate-pulse rounded bg-muted" />
        <div className="h-10 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <h2 className="text-lg font-heading font-semibold">Project not found</h2>
        <p className="mt-1 text-sm text-muted-foreground">This project may not exist or you don&apos;t have access.</p>
      </div>
    )
  }

  // Check if the current route is a locked feature
  const featureKey = getActiveFeatureKey(pathname, projectId!)
  const isLocked = featureKey && phase && !isFeatureAvailable(featureKey, phase)

  return (
    <ProjectContext.Provider value={{ project, userRole, projectId: projectId!, isLoading }}>
      <div className="-mx-4 -mt-6 sm:-mx-6 lg:-mx-8">
        <div className="px-4 pt-4 sm:px-6 lg:px-8">
          <Breadcrumbs
            items={[
              { label: 'Projects', href: '/hub/projects' },
              { label: project.name },
            ]}
            className="mb-3"
          />
        </div>
        <ProjectTabs projectId={projectId!} userRole={userRole} phase={phase} />
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <ErrorBoundaryClass level="project">
            {isLocked ? (
              <LockedFeature featureKey={featureKey} phase={phase} />
            ) : (
              <Outlet />
            )}
          </ErrorBoundaryClass>
        </div>
      </div>
    </ProjectContext.Provider>
  )
}

function LockedFeature({ featureKey, phase }: { featureKey: string; phase: Phase }) {
  const unlockPhase = getUnlockPhase(featureKey)
  const unlockLabel = unlockPhase ? PHASE_LABELS[unlockPhase] : null
  const currentLabel = PHASE_LABELS[phase]

  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-4">
        <Lock className="h-6 w-6 text-muted-foreground" />
      </div>
      <h2 className="text-lg font-heading font-semibold">Not yet available</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        {unlockLabel
          ? `This feature unlocks in the ${unlockLabel} phase. Your project is currently in the ${currentLabel} phase.`
          : `This feature is not available in the ${currentLabel} phase.`}
      </p>
    </div>
  )
}
